import type { WithTypeName } from "lib/apollo/types";
import type {
  MembershipDurationEnum,
  MembershipType,
  UserType,
  VisualRefType,
  VisualTypeEnum
} from "@shared/graphql.types";

export const MIGRATION_COOKIE = "beautypie-storefront-migration";
export const MEMBER_HOME_PAGE = "/";
export const DEFAULT_HOME_PAGE = "/";

export const NoAvatar: VisualRefType = {
  id: "no-avatar",
  url: "/none.jpg",
  name: "none",
  type: "image" as VisualTypeEnum,
  altTag: "none",
  usage: "full"
};

export const NoMembership: WithTypeName<MembershipType> = {
  id: "none:none",
  value: "none",
  duration: "none" as MembershipDurationEnum,
  name: "Non-Member",
  description:
    "You will buy products at typical price and miss out on all the savings",
  visual: NoAvatar,
  giftVisual: null,
  segment: "",
  price: 0,
  period: 0,
  frequency: 1,
  initEntitlement: 0,
  monthlyAllowance: 0,
  __typename: "MembershipType"
};

export const guestUser: WithTypeName<UserType> = {
  id: "initial",
  name: "guest",
  email: "guest@bp.com",
  membership: NoMembership,
  avatar: NoAvatar,
  spentSoFar: 0,
  carriedForward: 0,
  basketId: "",
  basketVersion: -1,
  basket: [],
  wishlistId: "",
  wishlistVersion: -1,
  wishlist: [],
  discounts: [],
  orderDraft: null,
  hints: [],
  reviews: [],
  dateOfBirth: null,
  MembershipEndDate: null,
  MembershipStartDate: null,
  registrationDateTime: null,
  pricing: null,
  includedDiscounts: [],
  rejectedDiscounts: [],
  TotalOrders: 0,
  LifetimeValueOrders: 0,
  LifetimeSubscriptionSaving: 0,
  deliveryType: "standard",
  isFreeTrialAllowed: true,
  __typename: "UserType"
};

export const activeMemberships = {
  "en-GB": {
    plusMembershipId: "3074457345616767232",
    plusMembershipWithStarterKitId: "3074457345616777230",
    staffMembershipId: "3074457345616769230",
    monthlyMembershipId: "3074457345616767234",
    monthlyMembership300Id: "3074457345616728317",
    giftMembershipId: "3074457345616768736",
    partnerGiftMembershipId: "3074457345616752730",
    freeTrialMembershipId: "3074457345616780730",
    freeTrialMembership30Id: "3074457345616770240",
    annualLimitlessMembershipId: "3074457345616802730",
    freeTrialLimitlessMembership30Id: "",
    monthlyLimitlessMembershipId: "3074457345616804240",
    threeMonthFreeTrialMembershipId: "3074457345616809730",
    threeMonthPaidRenewsAnnuallyId: "3074457345616815432",
    thirtyDayFreeTrialMembershipId: "3074457345616815743",
    thirtyDayFreeTrialRenewsToMonthlyMembershipId: "3074457345616815282",
    sixtyDayFreeTrialRenewsToMonthlyMembershipId: "3074457345616815322",
    sixtyDayFreeTrialRenewsToAnnualMembershipId: "3074457345617583401",
    annualLiteMembership: "3074457345614994501",
    freeTrial14DayRenewsToStarter12M: "BP_TRIAL_14D_STARTER_12M_UK",
    freeTrial30DayRenewsToStarter12M: "BP_TRIAL_30D_STARTER_12M_UK"
  },
  "en-US": {
    plusMembershipId: "3074457345616760237",
    plusMembershipWithStarterKitId: "3074457345616777232",
    staffMembershipId: "3074457345616769232",
    monthlyMembershipId: "3074457345616761749",
    monthlyMembership300Id: "3074457345616728352",
    giftMembershipId: "3074457345616760247",
    partnerGiftMembershipId: "3074457345616755231",
    freeTrialMembershipId: "3074457345616780732",
    freeTrialMembership30Id: "3074457345616770230",
    annualLimitlessMembershipId: "3074457345616795237",
    freeTrialLimitlessMembership30Id: "3074457345616796264",
    monthlyLimitlessMembershipId: "3074457345616795239",
    threeMonthFreeTrialMembershipId: "3074457345616810729",
    threeMonthPaidRenewsAnnuallyId: "3074457345616815434",
    thirtyDayFreeTrialMembershipId: "3074457345616815745",
    thirtyDayFreeTrialRenewsToMonthlyMembershipId: "3074457345616815284",
    sixtyDayFreeTrialRenewsToMonthlyMembershipId: "3074457345616815324",
    sixtyDayFreeTrialRenewsToAnnualMembershipId: "3074457345617583302",
    annualLiteMembership: "3074457345614994502",
    freeTrial14DayRenewsToStarter12M: "BP_TRIAL_14D_STARTER_12M_US",
    freeTrial30DayRenewsToStarter12M: "BP_TRIAL_30D_STARTER_12M_US"
  }
} as const;

export const activeNewSkusMemberships = {
  "en-GB": {
    annualLimitlessMembershipId: "BP_PLUS_UK",
    monthlyLimitlessMembershipId: "BP_MONTHLY_UK",
    thirtyDayFreeTrialMembershipId: "BP_TRIAL_30D_UK",
    thirtyDayFreeTrialRenewsToMonthlyMembershipId: "BP_TRIAL_30D_M_UK",
    sixtyDayFreeTrialRenewsToMonthlyMembershipId: "BP_TRIAL_60D_M_UK",
    sixtyDayFreeTrialRenewsToAnnualMembershipId: "BP_TRIAL_60D_UK"
  },
  "en-US": {
    annualLimitlessMembershipId: "BP_PLUS_US",
    monthlyLimitlessMembershipId: "BP_MONTHLY_US",
    thirtyDayFreeTrialMembershipId: "BP_TRIAL_30D_US",
    thirtyDayFreeTrialRenewsToMonthlyMembershipId: "BP_TRIAL_30D_M_US",
    sixtyDayFreeTrialRenewsToMonthlyMembershipId: "BP_TRIAL_60D_M_US",
    sixtyDayFreeTrialRenewsToAnnualMembershipId: "BP_TRIAL_60D_US"
  }
} as const;

export const activeVariants = {
  "en-GB": {
    giftMembershipProductId: "3074457345616768734"
  },
  "en-US": {
    giftMembershipProductId: "3074457345616760245"
  }
} as const;
